// frontend/src/pages/ContactUsPage.js

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Turnstile } from '@marsidev/react-turnstile';
import { Link } from 'react-router-dom';

// --- Imports ---
import './ContactUsPage.css';
import { useProfile } from '../hooks/useProfile'; 
// IMPORT: Needed to point to the correct API server
import { BACKEND_URL } from '../supabaseClient';

// --- Configuration ---
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "YOUR_CLOUDFLARE_SITE_KEY"; 

function ContactUsPage() {
  // 1. Get User Profile (Smart Auto-fill)
  const { profile } = useProfile(); 

  // --- Form Fields State ---
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [queryType, setQueryType] = useState('');
  const [orderId, setOrderId] = useState(''); 
  const [message, setMessage] = useState('');
  
  // --- Logic State ---
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const turnstileRef = useRef();
  
  // Define Limits
  const MIN_CHARS = 20;
  const MAX_CHARS = 1500;

  // 2. Auto-fill Effect
  useEffect(() => {
    if (profile) {
      if (profile.full_name) setName(profile.full_name);
      if (profile.email) setEmail(profile.email); 
    }
  }, [profile]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // 3. Validation Logic
    if (message.length < MIN_CHARS) {
      setStatus(`Please provide more detail (min ${MIN_CHARS} characters).`);
      return;
    }

    if (!token) {
      setStatus('Please complete the security check.');
      return;
    }

    setStatus('Sending...');
    setIsSending(true);

    // 4. Payload Construction
    const formData = {
      name,
      email,
      queryType, 
      orderId: queryType === 'billing_issue' ? orderId : undefined, 
      message,
      token
    };

    try {
      // Use full BACKEND_URL to avoid 405 "Wrong Door" Error
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);

      if (response.data.success) {
        setIsSubmitted(true);
        setMessage('');
        setQueryType('');
        setOrderId('');
        setToken(null);
        setStatus('');
      } else {
        setStatus(response.data.message || 'An error occurred.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      const errMsg = error.response?.data?.message || error.message || 'Server error. Please try again later.';
      setStatus(errMsg);
    } finally {
      setIsSending(false);
    }
  };

  const getStatusClass = () => {
    if (status.toLowerCase().includes('error') || status.toLowerCase().includes('please')) return 'status-error';
    if (status.toLowerCase().includes('sending')) return 'status-info';
    return '';
  };

  return (
    <div className="contact-page-wrapper">
      <div className="contact-container">
        
        {/* --- LEFT COLUMN: Contact Information --- */}
        <div className="contact-info-column">
          <h1>Get in touch</h1>
          <p className="contact-subtitle">
            Need help with your Credits, Subscription, or just have a feature request? 
            We categorize every ticket to ensure the right team member helps you faster.
          </p>

          {/* 1. Support Email */}
          <div className="info-item">
            <span className="info-icon">✉️</span>
            <div>
              <strong>Support Email</strong>
              <p>support@fullmockup.com</p>
            </div>
          </div>

          {/* 2. Phone Number (NEW) */}
          <div className="info-item">
            <span className="info-icon">📞</span>
            <div>
              <strong>Phone Support</strong>
              <p>+91-9560695885</p>
            </div>
          </div>

          {/* 3. Physical Address (UPDATED) */}
          <div className="info-item">
            <span className="info-icon">📍</span>
            <div>
              <strong>Physical Address</strong>
              <p>
                R-53A, Sir Syed Road, Jamia Nagar<br />
                Okhla, New Delhi, India - 110025
              </p>
            </div>
          </div>
          
          {/* 4. Business Hours */}
          <div className="info-item">
            <span className="info-icon">💼</span>
            <div>
              <strong>Business Hours</strong>
              <p>Mon - Fri, 10am - 5pm IST</p>
            </div>
          </div>
        </div>

        {/* --- RIGHT COLUMN: The Form --- */}
        <div className="contact-form-column">
          {isSubmitted ? (
            <div className="contact-success-box">
              <div className="success-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3>Message Sent!</h3>
              <p>Your ticket has been created. We usually reply within 24 hours.</p>
              <button onClick={() => setIsSubmitted(false)} className="btn btn-outline">
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="contact-form">
              
              {/* Row 1: Name & Email */}
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Jane Doe"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="jane@example.com"
                  className="form-input"
                />
              </div>

              {/* Row 2: Category Selection */}
              <div className="form-group">
                <label htmlFor="queryType">Topic / Category</label>
                <select
                  id="queryType"
                  value={queryType}
                  onChange={(e) => setQueryType(e.target.value)}
                  required
                  className="form-input"
                >
                  <option value="" disabled>Select a topic...</option>
                  <option value="billing_issue">Billing & Payments (High Priority)</option>
                  <option value="credits_issue">Credits & Usage Limits</option>
                  <option value="technical_bug">Report a Bug / Error</option>
                  <option value="account_support">Account & Login Support</option>
                  <option value="business_inquiry">Business & Enterprise</option>
                  <option value="feature_request">Feature Request / Feedback</option>
                  <option value="general">Other Inquiry</option>
                </select>
              </div>

              {/* Contextual Field: Order ID (Only shows for Billing) */}
              {queryType === 'billing_issue' && (
                <div className="form-group" style={{ animation: 'fadeIn 0.3s ease' }}>
                  <label htmlFor="orderId">Order ID / Transaction ID (Optional)</label>
                  <input
                    id="orderId"
                    type="text"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    placeholder="e.g. order_123456789"
                    className="form-input"
                  />
                  <small style={{ color: '#666', marginTop: '4px' }}>
                    Providing this helps us process your refund or query faster.
                  </small>
                </div>
              )}

              {/* Row 3: Message with Character Limit */}
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows="5"
                  maxLength={MAX_CHARS} // Limits the user input
                  placeholder="Please describe your issue in detail..."
                  className="form-input"
                />
                <div style={{ textAlign: 'right', fontSize: '0.8rem', marginTop: '5px', color: message.length < MIN_CHARS ? '#d32f2f' : '#666' }}>
                  {message.length} / {MAX_CHARS} characters
                </div>
              </div>

              {/* Security */}
              <div className="turnstile-container">
                <Turnstile 
                  siteKey={TURNSTILE_SITE_KEY} 
                  onSuccess={(token) => { setToken(token); setStatus(''); }}
                  onError={() => setStatus('Security check failed.')}
                  onExpire={() => setToken(null)}
                  ref={turnstileRef}
                />
              </div>

              {/* Status & Submit */}
              {status && <p className={`form-status ${getStatusClass()}`}>{status}</p>}

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={!token || isSending}
              >
                {isSending ? 'Sending...' : 'Send Message'}
              </button>

              {/* Privacy Note (Replaces Checkbox) */}
              <p className="privacy-note">
                We respect your privacy. By submitting the form you agree to our{' '}
                <Link to="/privacy-policy" className="privacy-link">Privacy Policy</Link>.
              </p>

            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContactUsPage;